import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import { ALERT_LEVELS } from '../utils/constants.js';
import SupervisorBackLink from '../components/SupervisorBackLink.jsx';
import './Alerts.css';

export default function Alerts() {
    const { user } = useAuth();
    const isSupervisor = user?.rol === 'supervisor';
    const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';
    const [searchParams] = useSearchParams();
    const [alerts, setAlerts] = useState([]);
    const [summary, setSummary] = useState(null);
    const levelFromUrl = searchParams.get('level');
    const initialFilter =
        levelFromUrl === 'red' || levelFromUrl === 'yellow' || levelFromUrl === 'green'
            ? levelFromUrl
            : 'all';
    const [filter, setFilter] = useState(initialFilter);
    const [loading, setLoading] = useState(true);
    const [intervention, setIntervention] = useState(null);
    const [actionText, setActionText] = useState('');
    const [activeTab, setActiveTab] = useState('dashboard'); // Tabs: 'dashboard' | 'list'
    const [alertRules, setAlertRules] = useState([]);

    const { tenantId } = useTenantScope();

    useEffect(() => {
        loadData();
    }, [tenantId]);

    useEffect(() => {
        if (levelFromUrl === 'red' || levelFromUrl === 'yellow' || levelFromUrl === 'green') {
            setFilter(levelFromUrl);
            setActiveTab('list');
        }
    }, [levelFromUrl]);

    async function loadData() {
        setLoading(true);
        try {
            const [alertsRes, summaryRes, rulesRes] = await Promise.all([
                api.getAlerts(),
                api.getAlertSummary(),
                api.getAlertRules().catch(() => ({ data: [] })),
            ]);
            setAlerts(alertsRes.data || []);
            setSummary(summaryRes.data || { summary: [], byArea: [] });
            setAlertRules(rulesRes?.data || []);
        } catch (err) {
            console.error('Error fetching alerts:', err);
            setAlerts([]);
        } finally {
            setLoading(false);
        }
    }

    async function handleAcknowledge(id) {
        await api.acknowledgeAlert(id);
        loadData();
    }

    async function handleIntervene(id) {
        if (!actionText.trim()) return;
        await api.interveneAlert(id, { actionTaken: actionText });
        setIntervention(null);
        setActionText('');
        loadData();
    }

    const filtered = filter === 'all' ? alerts : alerts.filter(a => a.level === filter);

    if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

    // Heatmap Preparation
    const renderHeatmap = () => {
        if (!summary || !summary.byArea) return <p className="text-muted">No hay datos de áreas.</p>;

        // Group by area
        const map = {};
        summary.byArea.forEach(d => {
            if (!map[d.area_name]) map[d.area_name] = { red: 0, yellow: 0, green: 0 };
            map[d.area_name][d.level] = d.count;
        });

        // Sorted areas: those with most reds first
        const sortedAreas = Object.keys(map).sort((a, b) => map[b].red - map[a].red || map[b].yellow - map[a].yellow);

        if (sortedAreas.length === 0) return <p className="text-muted text-center py-lg border-dashed">No hay alertas activas para visualizar.</p>;

        return (
            <div className="heatmap-container glass-card stagger">
                <h3 className="mb-md">🗺️ Mapa de Calor (Áreas Críticas)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 2fr) 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.8rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>
                    <div>Área</div>
                    <div style={{ textAlign: 'center' }}>Rojas (Críticas)</div>
                    <div style={{ textAlign: 'center' }}>Amarillas (Media)</div>
                    <div style={{ textAlign: 'center' }}>Verdes (Info)</div>
                </div>
                {sortedAreas.map(area => {
                    const data = map[area];
                    return (
                        <div key={area} style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 2fr) 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                            <div style={{ fontWeight: '500' }}>{area}</div>
                            <div style={{ backgroundColor: data.red > 0 ? 'rgba(255, 59, 48, 0.2)' : 'transparent', color: data.red > 0 ? 'var(--color-alert-red)' : 'var(--color-text-muted)', padding: '0.5rem', borderRadius: '4px', textAlign: 'center', border: data.red > 0 ? '1px solid rgba(255, 59, 48, 0.4)' : '1px solid rgba(0,0,0,0.05)' }}>
                                {data.red > 0 ? `${data.red} 🚨` : '0'}
                            </div>
                            <div style={{ backgroundColor: data.yellow > 0 ? 'rgba(255, 204, 0, 0.2)' : 'transparent', color: data.yellow > 0 ? 'var(--color-alert-yellow)' : 'var(--color-text-muted)', padding: '0.5rem', borderRadius: '4px', textAlign: 'center', border: data.yellow > 0 ? '1px solid rgba(255, 204, 0, 0.4)' : '1px solid rgba(0,0,0,0.05)' }}>
                                {data.yellow > 0 ? `${data.yellow} ⚠️` : '0'}
                            </div>
                            <div style={{ backgroundColor: data.green > 0 ? 'rgba(52, 199, 89, 0.2)' : 'transparent', color: data.green > 0 ? 'var(--color-alert-green)' : 'var(--color-text-muted)', padding: '0.5rem', borderRadius: '4px', textAlign: 'center', border: data.green > 0 ? '1px solid rgba(52, 199, 89, 0.4)' : '1px solid rgba(0,0,0,0.05)' }}>
                                {data.green > 0 ? `${data.green} ℹ️` : '0'}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="container container-wide animate-in">
            {isSupervisor && <SupervisorBackLink />}
            <h2>🚨 Monitoreo y Alertas</h2>
            <p className="text-muted mb-lg">El sistema nervioso central de ALINEA — Centralizando las señales preventivas</p>

            {alertRules.length > 0 && (
                <details className="glass-card mb-lg" style={{ padding: '1rem 1.25rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                        ¿Cuándo se genera una alerta?
                    </summary>
                    <ul style={{ margin: '1rem 0 0', paddingLeft: '1.25rem', lineHeight: 1.6 }}>
                        {alertRules.map((r) => (
                            <li key={r.code} className="mb-sm">
                                <strong>{r.code} — {r.title}:</strong> {r.rule}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            <div className="tabs mb-lg" style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: '0.5rem' }}>
                <button
                    className={`btn ${activeTab === 'dashboard' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    📊 Tablero de Control
                </button>
                <button
                    className={`btn ${activeTab === 'list' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('list')}
                >
                    📋 Gestión de Alertas
                    {alerts.filter(a => a.status === 'active').length > 0 &&
                        <span style={{ background: 'var(--color-alert-red)', color: 'white', padding: '0 6px', borderRadius: '10px', fontSize: '0.75rem', marginLeft: '6px' }}>
                            {alerts.filter(a => a.status === 'active').length}
                        </span>
                    }
                </button>
            </div>

            {activeTab === 'dashboard' && (
                <div className="stagger">
                    {/* Summary badges map */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                        {['red', 'yellow', 'green'].map(level => {
                            const count = summary?.summary?.find(s => s.level === level && s.status === 'active')?.count || 0;
                            return (
                                <div key={level} className="glass-card" style={{ borderLeft: `4px solid ${ALERT_LEVELS[level].color}` }}>
                                    <h4 style={{ color: 'var(--color-text-secondary)' }}>{ALERT_LEVELS[level].emoji} Alertas {ALERT_LEVELS[level].label}</h4>
                                    <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: '0.5rem 0', color: ALERT_LEVELS[level].color }}>{count}</p>
                                    <p className="text-sm text-muted">{level === 'red' ? 'Requiere Intervención (< 24h)' : level === 'yellow' ? 'Señales de deterioro (< 3 días)' : 'Estabilidad (Reconocimiento)'}</p>
                                </div>
                            );
                        })}
                    </div>
                    {isAdmin && renderHeatmap()}
                </div>
            )}

            {activeTab === 'list' && (
                <div className="stagger">
                    <div className="alerts-summary mb-lg" style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                        <button
                            className={`badge alerts-filter-btn ${filter === 'all' ? 'active' : ''}`}
                            style={{ backgroundColor: filter === 'all' ? 'var(--color-text-primary)' : 'rgba(0,0,0,0.05)', color: filter === 'all' ? '#fff' : 'inherit', border: 'none', cursor: 'pointer' }}
                            onClick={() => setFilter('all')}
                        >
                            Todas {alerts.filter(a => a.status === 'active').length}
                        </button>
                        {['red', 'yellow', 'green'].map(level => (
                            <button
                                key={level}
                                className={`badge badge-${level} alerts-filter-btn ${filter === level ? 'active' : ''}`}
                                style={{ opacity: filter === 'all' || filter === level ? 1 : 0.5, cursor: 'pointer', border: 'none' }}
                                onClick={() => setFilter(f => f === level ? 'all' : level)}
                            >
                                {ALERT_LEVELS[level].emoji} {alerts.filter(a => a.level === level && a.status === 'active').length}
                            </button>
                        ))}
                    </div>

                    <div className="alerts-list">
                        {filtered.length === 0 && <p className="text-muted text-center mt-xl border-dashed py-lg">No se encontraron alertas en la bandeja.</p>}

                        {filtered.map(alert => (
                            <div key={alert.id} className={`glass-card alert-card alert-card-${alert.level}`}>
                                <div className="alert-card-header">
                                    <div className="flex items-center gap-sm">
                                        <span className={`badge badge-${alert.level}`}>{alert.alert_code}</span>
                                        <span className={`badge badge-${alert.level}`}>{ALERT_LEVELS[alert.level]?.label}</span>
                                    </div>
                                    <span className="text-xs text-muted">
                                        Generada: {new Date(alert.created_at).toLocaleString('es')}
                                    </span>
                                </div>

                                <h4 className="alert-card-title">{alert.title}</h4>

                                {alert.employee_name && (
                                    <p className="text-sm text-muted"><span style={{ width: '20px', display: 'inline-block' }}>👤</span> {alert.employee_name} ({alert.employee_code})</p>
                                )}
                                {alert.area_name && (
                                    <p className="text-sm text-muted"><span style={{ width: '20px', display: 'inline-block' }}>📍</span> {alert.area_name}</p>
                                )}

                                <p className="alert-card-desc text-sm" style={{ borderLeft: `2px solid ${ALERT_LEVELS[alert.level].color}`, paddingLeft: '0.5rem', marginTop: '0.5rem' }}>
                                    {alert.description}
                                </p>

                                <div className="alert-card-status mt-md">
                                    <span className={`badge badge-${alert.status === 'active' ? alert.level : 'green'}`} style={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                        {alert.status === 'active' ? '● Requiere Atención' : alert.status === 'acknowledged' ? '👁 Vista por Supervisor' : '✓ Intervenida'}
                                    </span>
                                </div>

                                {alert.status === 'active' && (
                                    <div className="alert-card-actions mt-sm" style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                        <button className="btn btn-secondary btn-sm" onClick={() => handleAcknowledge(alert.id)}>
                                            👁 Marcar Vista
                                        </button>
                                        <button className="btn btn-primary btn-sm" onClick={() => setIntervention(alert.id)}>
                                            🤝 Registrar Intervención
                                        </button>
                                    </div>
                                )}

                                {intervention === alert.id && (
                                    <div className="alert-intervention animate-in mt-sm" style={{ backgroundColor: 'rgba(0,0,0,0.03)', padding: '1rem', borderRadius: '8px' }}>
                                        <label className="text-sm" style={{ display: 'block', marginBottom: '0.5rem' }}>Describa la acción tomada o contacto realizado:</label>
                                        <textarea
                                            className="input"
                                            rows="3"
                                            placeholder="Detalle de la intervención realizada para contener el riesgo..."
                                            value={actionText}
                                            onChange={e => setActionText(e.target.value)}
                                        />
                                        <div className="flex gap-sm mt-sm">
                                            <button className="btn btn-primary btn-sm" onClick={() => handleIntervene(alert.id)}>
                                                Guardar Caso
                                            </button>
                                            <button className="btn btn-secondary btn-sm" onClick={() => { setIntervention(null); setActionText(''); }}>
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
